/**
 * Audience-based filter for UIState projections.
 *
 * filterUIStateForAudience is a pure post-processing function that takes
 * the authoritative UIState (from buildUIState) and produces an
 * audience-appropriate view by redacting or replacing fields.
 *
 * One UIState, filtered views — no alternate game states.
 * Implements D-0302 (Single UIState, Multiple Audiences).
 *
 * No boardgame.io imports. No registry imports. No LegendaryGameState.
 * No .reduce(). No mutation of input. No I/O.
 *
 * WP-128 / EC-131 — extends the redaction matrix for the new per-player
 * fields. `inPlayCards` / `inPlayDisplay` redacted for `audience !==
 * ownPlayerId` and for `'spectator'` (D-12803). `discardTopCard` /
 * `victoryCards` / `victoryVP` are public and pass through every
 * audience (D-12803). Shared-board fields (mastermind / scheme / city /
 * decks / piles / koPile) are public and pass through unchanged with
 * per-entry shallow copies (WP-111 D-11105 aliasing-defense).
 */
// why: non-active players and spectators must not see the active player's
// remaining resources (attack/recruit/piercing/woundsDrawn). Zeroed
// economy prevents strategic information leakage while maintaining type
// stability. WP-128 extends the sentinel with the new `piercing` and
// `woundsDrawn` fields that follow the same active-player-only redaction.
const REDACTED_ECONOMY = {
    attack: 0,
    recruit: 0,
    availableAttack: 0,
    availableRecruit: 0,
    piercing: 0,
    woundsDrawn: 0,
};
/**
 * Copies the restricted-attack projection entries (WP-790 / D-24652), each with
 * its own copied `targets` array, so the filtered UIState never aliases the input.
 *
 * @param grants - The built projection's restricted-attack entries.
 * @returns A deep copy of the entries.
 */
function copyRestrictedAttackProjection(grants) {
    const copied = [];
    for (const grant of grants) {
        copied.push({ remaining: grant.remaining, targets: [...grant.targets], label: grant.label });
    }
    return copied;
}
/**
 * Builds a per-element shallow-copy of City spaces, including the
 * additive `display` payload, to prevent aliasing with the input
 * UIState. Public information — not redacted.
 */
// why: WP-214 — villainAttachedHeroes is public; per-entry spread copies
// prevent aliasing with the input UIState
function buildVillainAttachedHeroesFilterCopy(villainAttachedHeroes) {
    if (!villainAttachedHeroes) {
        return {};
    }
    const result = {};
    for (const villainId of Object.keys(villainAttachedHeroes)) {
        const heroes = villainAttachedHeroes[villainId];
        if (heroes !== undefined) {
            result[villainId] = [...heroes];
        }
    }
    return result;
}
// why: WP-111 — City display is public; shallow copies at every level
// prevent aliasing with the input UIState's card objects. Mirrors the
// WP-028 cardKeywords post-mortem aliasing-prevention precedent.
function deepCopyCitySpaces(spaces) {
    const result = [];
    for (const space of spaces) {
        if (space === null) {
            result.push(null);
        }
        else {
            result.push({
                extId: space.extId,
                type: space.type,
                keywords: [...space.keywords],
                display: { ...space.display },
                attachedHeroes: [...space.attachedHeroes],
                // why: WP-505 — captured-card display is public board state; per-entry
                // shallow copy prevents aliasing, mirroring the display copy above. Both
                // new fields MUST pass through here or the audience filter silently
                // drops them (the EC-206 / PR #1165 whitelist failure mode).
                attachedHeroDisplay: space.attachedHeroDisplay.map((entry) => ({ ...entry })),
                attachedBystanderCount: space.attachedBystanderCount,
                fightCost: space.fightCost,
                // why: WP-817 / D-24750 — the public fight discard cost MUST pass through here
                // or the whitelist drops it (the EC-206 / PR #1165 failure mode). Conditional
                // spread (never a literal undefined) with a fresh array copy.
                ...(space.fightDiscardCosts !== undefined
                    ? { fightDiscardCosts: [...space.fightDiscardCosts] }
                    : {}),
            });
        }
    }
    return result;
}
/**
 * Builds a per-element shallow-copy of HQ slotDisplay entries to
 * prevent aliasing with the input UIState. Public information — not
 * redacted.
 */
// why: WP-111 — HQ slotDisplay is public; per-entry shallow copies at
// the filter boundary mirror the projection-time aliasing-prevention
// pattern (WP-028 cardKeywords post-mortem precedent).
function deepCopyHqSlotDisplay(slotDisplay) {
    const result = [];
    for (const entry of slotDisplay) {
        if (entry === null) {
            result.push(null);
        }
        else {
            result.push({
                extId: entry.extId,
                display: { ...entry.display },
            });
        }
    }
    return result;
}
/**
 * Builds a per-entry copy of the HQ haunters (WP-757 / D-24587) so the filtered
 * UIState shares no references with its input. Public information — not redacted.
 *
 * @param haunters - The projected per-slot haunters.
 * @returns A fresh array with fresh entries (and fresh display payloads).
 */
function deepCopyHqHaunters(haunters) {
    const result = [];
    for (const haunter of haunters) {
        if (haunter === null) {
            result.push(null);
        }
        else if (haunter.kind === 'villain') {
            result.push({ kind: 'villain', extId: haunter.extId, display: { ...haunter.display } });
        }
        else {
            result.push({ kind: 'mastermind' });
        }
    }
    return result;
}
// why: WP-128 / D-12805 — per-entry shallow copy of UIDisplayEntry[] at
// the filter boundary. Mirrors deepCopyCitySpaces / deepCopyHqSlotDisplay
// pattern: every entry is a fresh `{ extId, display }` object with
// display itself shallow-cloned. Used by victoryCards / strikePile /
// twistPile / escapedPile / koPile.cards / attachedBystanders.
/**
 * Per-entry shallow copy of UIDisplayEntry[] for filter aliasing-defense.
 */
function deepCopyDisplayEntries(entries) {
    const result = [];
    for (const entry of entries) {
        result.push({ extId: entry.extId, display: { ...entry.display } });
    }
    return result;
}
// why: WP-128 / D-12804 — shallow-copy the koPile shape; topCard is
// either null or a freshly-cloned UIDisplayEntry; cards is per-entry
// cloned via deepCopyDisplayEntries.
/**
 * Builds a deep-copied UIKoPileState from the source.
 */
function deepCopyKoPile(koPile) {
    let topCard;
    if (koPile.topCard === null) {
        topCard = null;
    }
    else {
        topCard = {
            extId: koPile.topCard.extId,
            display: { ...koPile.topCard.display },
        };
    }
    return {
        count: koPile.count,
        topCard,
        cards: deepCopyDisplayEntries(koPile.cards),
    };
}
/**
 * Builds a redacted copy of a UIPlayerState with private fields removed.
 *
 * Redacted fields (omitted): handCards, handDisplay, inPlayCards,
 * inPlayDisplay, discardCards, discardDisplay. Preserved fields (public
 * information): all counts, discardTopCard, victoryCards, victoryVP.
 *
 * @param player - The source player state. Not mutated.
 * @returns A new UIPlayerState with private fields omitted.
 */
function redactHandCards(player) {
    const base = {
        playerId: player.playerId,
        deckCount: player.deckCount,
        handCount: player.handCount,
        discardCount: player.discardCount,
        inPlayCount: player.inPlayCount,
        victoryCount: player.victoryCount,
        woundCount: player.woundCount,
        // why: hand card ext_ids redacted — this player's hand contents are
        // hidden from the viewing audience. Only handCount remains visible.
        // why: WP-111 / EC-118 — handDisplay omitted alongside handCards
        // (privacy symmetry). Leaking display data is identical to leaking
        // the CardExtId for opponent privacy purposes. Conditional
        // assignment is moot here because we simply do not assign the
        // field at all (mirrors the existing handCards omission pattern).
        // why: WP-128 / D-12803 — inPlayCards / inPlayDisplay redacted
        // for non-self / spectator audiences. Same omit-don't-assign
        // pattern as handCards for `exactOptionalPropertyTypes` discipline.
        // why: WP-243 / D-24010 — discardCards / discardDisplay redacted for
        // non-self / spectator audiences (same handCards privacy posture). The
        // full discard list carries card identities the owning player may act on
        // (KO-a-Hero); opponents and spectators see discardCount + discardTopCard
        // only. Omit-don't-assign, both fields together.
        // why: WP-606 / D-24417 — deckComposition (own draw-pool composition)
        // redacted for non-self / spectator (same posture): opponents see
        // deckCount only. Omit-don't-assign.
        // why: WP-608 / D-24419 — deckCardStats (own draw-pool card stats) redacted
        // for non-self / spectator (same posture): the map keys reveal the owner's
        // pool composition. Omit-don't-assign.
    };
    // why: WP-128 / D-12803 — discardTopCard is public information (face-up
    // at the physical table). Pass through with per-entry shallow copy
    // to prevent aliasing. Distinguish optional (undefined = redacted by
    // some other layer) from null (visible-but-empty: `discardCount === 0`).
    if (player.discardTopCard !== undefined) {
        if (player.discardTopCard === null) {
            base.discardTopCard = null;
        }
        else {
            base.discardTopCard = {
                extId: player.discardTopCard.extId,
                display: { ...player.discardTopCard.display },
            };
        }
    }
    // why: WP-128 / D-12803 — victoryCards is public knowledge by design
    // (VP is built from face-up resolved cards). Per-entry shallow copy
    // mirrors the projection-time aliasing-defense.
    if (player.victoryCards !== undefined) {
        base.victoryCards = deepCopyDisplayEntries(player.victoryCards);
    }
    // why: WP-128 / D-12801 — victoryVP is public; primitive number, no
    // aliasing concern.
    if (player.victoryVP !== undefined) {
        base.victoryVP = player.victoryVP;
    }
    // why: WP-803 / D-24692 — outOfTimeCards is PUBLIC (the victoryCards disposition): the card
    // was played face-up and is set aside in the open. Per-entry shallow copy mirrors the
    // projection-time aliasing-defense.
    if (player.outOfTimeCards !== undefined) {
        base.outOfTimeCards = deepCopyDisplayEntries(player.outOfTimeCards);
    }
    // why: WP-858 / D-24794 — controlledArtifacts is PUBLIC (the outOfTimeCards disposition): the
    // Ritual Artifacts sit in front of the player in the open. Per-entry shallow copy.
    if (player.controlledArtifacts !== undefined) {
        base.controlledArtifacts = deepCopyDisplayEntries(player.controlledArtifacts);
    }
    // why: WP-848 / D-24784 — conditionalClauses is PUBLIC: every outcome it counts is
    // already in the public game log. Fresh object so no audience aliases the built state.
    if (player.conditionalClauses !== undefined) {
        base.conditionalClauses = {
            played: player.conditionalClauses.played,
            assembled: player.conditionalClauses.assembled,
        };
    }
    return base;
}
/**
 * Builds a copy of a UIPlayerState preserving all hand/in-play/public fields.
 *
 * @param player - The source player state. Not mutated.
 * @returns A new UIPlayerState with all fields preserved (per-entry shallow-copied).
 */
function preserveHandCards(player) {
    const base = {
        playerId: player.playerId,
        deckCount: player.deckCount,
        handCount: player.handCount,
        discardCount: player.discardCount,
        inPlayCount: player.inPlayCount,
        victoryCount: player.victoryCount,
        woundCount: player.woundCount,
    };
    // why: active player sees own hand card ext_ids for gameplay.
    // Spread copy prevents aliasing with input UIState.
    if (player.handCards !== undefined) {
        base.handCards = [...player.handCards];
    }
    // why: WP-111 / EC-118 — conditional assignment per WP-029 D-2902
    // exactOptionalPropertyTypes precedent (do NOT assign undefined
    // literally; do NOT use inline ternary that returns T[] | undefined).
    // Per-entry shallow copy of each UICardDisplay prevents aliasing with
    // the input UIState's display objects (same WP-028 cardKeywords
    // post-mortem precedent applied at the filter boundary).
    if (player.handDisplay !== undefined) {
        const copiedHandDisplay = [];
        for (const display of player.handDisplay) {
            copiedHandDisplay.push({ ...display });
        }
        base.handDisplay = copiedHandDisplay;
    }
    // why: WP-817 / D-24750 — handCosts is owner-only exactly like handCards /
    // handDisplay: copied here for the owning audience, never assigned by
    // redactHandCards. Conditional assignment, fresh copy.
    if (player.handCosts !== undefined) {
        base.handCosts = [...player.handCosts];
    }
    // why: WP-128 / D-12803 — active player sees own inPlayCards /
    // inPlayDisplay; same conditional-assignment pattern as handCards.
    // Spread copy prevents aliasing with input UIState.
    if (player.inPlayCards !== undefined) {
        base.inPlayCards = [...player.inPlayCards];
    }
    if (player.inPlayDisplay !== undefined) {
        const copiedInPlayDisplay = [];
        for (const display of player.inPlayDisplay) {
            copiedInPlayDisplay.push({ ...display });
        }
        base.inPlayDisplay = copiedInPlayDisplay;
    }
    // why: WP-243 / D-24010 — active player sees own full discard contents
    // (needed for the KO-a-Hero prompt + the "View all" discard view); same
    // conditional-assignment pattern as handCards. discardCards + discardDisplay
    // are preserved together (both or neither) and spread/per-entry copied to
    // prevent aliasing with the input UIState.
    if (player.discardCards !== undefined) {
        base.discardCards = [...player.discardCards];
    }
    if (player.discardDisplay !== undefined) {
        const copiedDiscardDisplay = [];
        for (const display of player.discardDisplay) {
            copiedDiscardDisplay.push({ ...display });
        }
        base.discardDisplay = copiedDiscardDisplay;
    }
    // why: WP-606 / D-24417 — active player sees their own draw-pool composition
    // (so the Deck Probability Panel can compute draw odds); redacted for
    // non-self / spectator (the discardCards privacy posture). Conditional
    // assignment + fresh array copy (never a literal `undefined`) satisfies
    // exactOptionalPropertyTypes and prevents aliasing with the input UIState. It
    // is already order-stripped at build time; the draw sequence never leaks.
    if (player.deckComposition !== undefined) {
        base.deckComposition = [...player.deckComposition];
    }
    // why: WP-608 / D-24419 — active player sees their own draw-pool card stats
    // (recruit/attack/cost per pool card) for the hand-projection Monte Carlo;
    // redacted for non-self / spectator (the deckComposition posture — the keys
    // reveal the owner's pool). Fresh DEEP copy (new object + per-entry
    // `{ ...stat }`) prevents aliasing with the input UIState; conditional
    // assignment (never a literal `undefined`) satisfies exactOptionalPropertyTypes.
    if (player.deckCardStats !== undefined) {
        const copiedDeckCardStats = {};
        for (const [poolCardExtId, cardStat] of Object.entries(player.deckCardStats)) {
            copiedDeckCardStats[poolCardExtId] = { ...cardStat };
        }
        base.deckCardStats = copiedDeckCardStats;
    }
    // why: WP-128 / D-12803 — discardTopCard is public information; same
    // copy treatment as in redactHandCards above. Optional+nullable combo
    // distinguishes redacted (`undefined`) from visible-but-empty (`null`).
    if (player.discardTopCard !== undefined) {
        if (player.discardTopCard === null) {
            base.discardTopCard = null;
        }
        else {
            base.discardTopCard = {
                extId: player.discardTopCard.extId,
                display: { ...player.discardTopCard.display },
            };
        }
    }
    // why: WP-128 / D-12803 — victoryCards is public; per-entry shallow
    // copy mirrors the projection-time aliasing-defense.
    if (player.victoryCards !== undefined) {
        base.victoryCards = deepCopyDisplayEntries(player.victoryCards);
    }
    // why: WP-128 / D-12801 — victoryVP is public; primitive number.
    if (player.victoryVP !== undefined) {
        base.victoryVP = player.victoryVP;
    }
    // why: WP-803 / D-24692 — outOfTimeCards is PUBLIC (the victoryCards disposition); per-entry
    // shallow copy mirrors the projection-time aliasing-defense.
    if (player.outOfTimeCards !== undefined) {
        base.outOfTimeCards = deepCopyDisplayEntries(player.outOfTimeCards);
    }
    // why: WP-858 / D-24794 — controlledArtifacts is PUBLIC (the outOfTimeCards disposition): the
    // Ritual Artifacts sit in front of the player in the open. Per-entry shallow copy.
    if (player.controlledArtifacts !== undefined) {
        base.controlledArtifacts = deepCopyDisplayEntries(player.controlledArtifacts);
    }
    // why: WP-848 / D-24784 — conditionalClauses is PUBLIC (same disposition as in
    // redactHandCards); fresh object so the owner view never aliases the built state.
    if (player.conditionalClauses !== undefined) {
        base.conditionalClauses = {
            played: player.conditionalClauses.played,
            assembled: player.conditionalClauses.assembled,
        };
    }
    return base;
}
/**
 * Filters a UIState for a specific audience.
 *
 * Pure function: no I/O, no mutation of input UIState, no side effects.
 * Same inputs always produce the same output.
 *
 * One UIState, filtered views — no alternate game states.
 * Implements D-0302 (Single UIState, Multiple Audiences).
 *
 * Forbidden behaviors (do not add later):
 * - mutation of the input uiState
 * - accessing G, ctx, or any engine internals
 * - caching or memoization
 * - any form of side effect
 *
 * @param uiState - The authoritative UIState from buildUIState. Not mutated.
 * @param audience - Who is viewing: player (with playerId) or spectator.
 * @returns A new UIState with audience-appropriate visibility.
 */
export function filterUIStateForAudience(uiState, audience) {
    // --- 1. Filter players based on audience ---
    const filteredPlayers = [];
    if (audience.kind === 'player') {
        for (const player of uiState.players) {
            if (player.playerId === audience.playerId) {
                // why: viewing player sees own hand card ext_ids
                filteredPlayers.push(preserveHandCards(player));
            }
            else {
                // why: other players' hand contents are hidden — count only
                filteredPlayers.push(redactHandCards(player));
            }
        }
    }
    else {
        // why: spectators see hand counts only — no hand card ext_ids for any player
        for (const player of uiState.players) {
            filteredPlayers.push(redactHandCards(player));
        }
    }
    // --- 2. Determine economy visibility ---
    let economy;
    if (audience.kind === 'player' && audience.playerId === uiState.game.activePlayerId) {
        // why: only the active player sees their own economy. WP-128 extends
        // with `piercing` / `woundsDrawn` — same active-player-only posture.
        economy = {
            attack: uiState.economy.attack,
            recruit: uiState.economy.recruit,
            availableAttack: uiState.economy.availableAttack,
            availableRecruit: uiState.economy.availableRecruit,
            piercing: uiState.economy.piercing,
            woundsDrawn: uiState.economy.woundsDrawn,
            // why: WP-581 / D-24390 — pass the recruit-as-attack cue flag through for
            // the ACTIVE player only, omit-when-absent (conditional, never a
            // `recruitSpendableAsAttack: undefined` literal). A field that reaches
            // buildUIState but not this whitelist is silently dropped (the EC-206
            // failure). REDACTED_ECONOMY (non-active players + spectators) never carries it.
            ...(uiState.economy.recruitSpendableAsAttack === true
                ? { recruitSpendableAsAttack: true }
                : {}),
            // why: WP-739 / D-24560 — pass the Excessive Violence availability cue
            // through for the ACTIVE player only, omit-when-absent (conditional, never
            // an `excessiveViolenceAvailable: undefined` literal). A field that reaches
            // buildUIState but not this whitelist is silently dropped (the EC-206
            // failure). REDACTED_ECONOMY (non-active players + spectators) never carries it.
            ...(uiState.economy.excessiveViolenceAvailable === true
                ? { excessiveViolenceAvailable: true }
                : {}),
            // why: WP-819 / D-24753 — pass Baron Zemo's rescue-on-fight cue through for the
            // ACTIVE player only, omit-when-absent, exactly like the Excessive Violence cue. The
            // filter is a whitelist: a field it does not name is silently dropped (EC-206), and
            // the WP-820 control would never appear. REDACTED_ECONOMY never carries it.
            ...(uiState.economy.rescueOnFightAvailable === true
                ? { rescueOnFightAvailable: true }
                : {}),
            // why: WP-842 / D-24776 — pass the once-per-turn Sidekick recruit cue through for the
            // ACTIVE player only, omit-when-absent. The filter is a whitelist: a field it does not
            // name is silently dropped (EC-206), and the Sidekicks cell would never disable after a
            // recruit. REDACTED_ECONOMY never carries it.
            ...(uiState.economy.hasRecruitedSidekickThisTurn === true
                ? { hasRecruitedSidekickThisTurn: true }
                : {}),
            // why: WP-783 / D-24629 — the Phasing options name cards in the owner's hand, so
            // they pass through for the ACTIVE player only, as a copied array, omit-when-absent.
            // REDACTED_ECONOMY (non-active players + spectators) never carries them. A field that
            // reaches buildUIState but not this whitelist is silently dropped (the EC-206 failure).
            ...(uiState.economy.phasingOptions !== undefined && uiState.economy.phasingOptions.length > 0
                ? { phasingOptions: [...uiState.economy.phasingOptions] }
                : {}),
            // why: WP-858 / D-24794 — the usable Ritual Artifacts pass through for the ACTIVE player only
            // (the phasingOptions disposition), as a copied array, omit-when-absent. REDACTED_ECONOMY
            // (non-active players + spectators) never carries them.
            ...(uiState.economy.ritualArtifactOptions !== undefined && uiState.economy.ritualArtifactOptions.length > 0
                ? { ritualArtifactOptions: [...uiState.economy.ritualArtifactOptions] }
                : {}),
            // why: WP-790 / D-24652 — pass the restricted-attack grants through for the ACTIVE player
            // only (Board-Visible Field Rule step 3), beside the recruit-as-attack cue, omit-when-absent,
            // copying each entry and its `targets`. REDACTED_ECONOMY (non-active players + spectators)
            // never carries them. A field that reaches buildUIState but not this whitelist is silently
            // dropped (the EC-206 failure) and the client would disable affordable Fight buttons.
            ...(uiState.economy.restrictedAttack !== undefined && uiState.economy.restrictedAttack.length > 0
                ? { restrictedAttack: copyRestrictedAttackProjection(uiState.economy.restrictedAttack) }
                : {}),
        };
    }
    else {
        // why: non-active players and spectators do not see economy details
        economy = { ...REDACTED_ECONOMY };
    }
    // --- 3. Build new UIState with all fields copied (no references to input) ---
    // why: deck contents/order are already hidden by buildUIState (WP-028) —
    // decks are projected as counts only, never as card arrays. The filter
    // does not need to redact deck data because it was never included.
    // why: WP-111 — City `display`, HQ `slotDisplay`, and Mastermind
    // `display` are public information and pass through. Per-entry
    // shallow copies (via deepCopyCitySpaces / deepCopyHqSlotDisplay)
    // prevent aliasing with the input UIState's card display objects.
    // Mastermind is shallow-copied with a nested {...display} to copy
    // the display payload alongside id / tacticsRemaining / tacticsDefeated.
    // why: WP-128 / D-12806 — `mastermind.attachedBystanders`,
    // `mastermind.strikePile`, `scheme.twistPile`, `city.escapedPile`,
    // `koPile` are public and pass through with per-entry shallow copies.
    // `decks` and `piles` are primitive-only; plain shallow copy.
    const result = {
        game: { ...uiState.game },
        players: filteredPlayers,
        city: {
            spaces: deepCopyCitySpaces(uiState.city.spaces),
            escapedPile: deepCopyDisplayEntries(uiState.city.escapedPile),
        },
        hq: {
            slots: [...uiState.hq.slots],
            ...(uiState.hq.slotDisplay !== undefined
                ? { slotDisplay: deepCopyHqSlotDisplay(uiState.hq.slotDisplay) }
                : {}),
            // why: WP-765 / D-24598 — dayNight is public shared-board state (derived from the public
            // HQ), passed through for every audience; omit-when-absent keeps non-day/night matches
            // byte-identical.
            ...(uiState.hq.dayNight !== undefined ? { dayNight: uiState.hq.dayNight } : {}),
            // why: WP-757 / D-24587 — the Haunt haunters are public shared-board information
            // for every audience and MUST survive this field-by-field whitelist. Optional, so
            // TypeScript does not flag a missing pass-through (the EC-206 drop). Conditional
            // spread keeps it omit-when-absent; fresh copies prevent aliasing.
            ...(uiState.hq.haunters !== undefined
                ? { haunters: deepCopyHqHaunters(uiState.hq.haunters) }
                : {}),
        },
        mastermind: {
            id: uiState.mastermind.id,
            tacticsRemaining: uiState.mastermind.tacticsRemaining,
            tacticsDefeated: uiState.mastermind.tacticsDefeated,
            display: { ...uiState.mastermind.display },
            attachedBystanders: deepCopyDisplayEntries(uiState.mastermind.attachedBystanders),
            strikePile: deepCopyDisplayEntries(uiState.mastermind.strikePile),
            // why: WP-399 / D-24202 — Hypno-Thralls are public shared-board
            // information (like attachedBystanders / strikePile above), so they must
            // survive this field-by-field whitelist. It is a REQUIRED field, but the
            // Board-Visible Field Rule still applies: a field populated in
            // buildUIState but not passed through here is silently dropped at the
            // filter (the EC-206 scheme.display / gameText failure mode). Fresh
            // deep-copied entries prevent aliasing with the input UIState.
            hypnoThralls: deepCopyDisplayEntries(uiState.mastermind.hypnoThralls),
            // why: EC-206 — the mastermind's Master-Strike / special-rules text is
            // public shared-board information (like mastermind.display above) and
            // must survive this whitelist. It is optional in UIMastermindState, so
            // TypeScript does not flag its omission; without this pass-through the
            // field is dropped here and the CardReaderModal renders "No rules text
            // available for this card" for every match. Conditional spread (never a
            // `gameText: undefined` literal) satisfies exactOptionalPropertyTypes;
            // fresh array copy prevents aliasing with the input UIState.
            ...(uiState.mastermind.gameText !== undefined
                ? { gameText: [...uiState.mastermind.gameText] }
                : {}),
            // why: WP-687 / D-24504 — the Final Blow "fight the Mastermind again" flag is
            // public shared-board information (like tacticsRemaining / tacticsDefeated
            // above), so it MUST survive this field-by-field whitelist. It is optional in
            // UIMastermindState, so TypeScript does NOT flag its omission; without this
            // pass-through the field is dropped here and every Final Blow match renders the
            // Mastermind permanently locked after the 4th tactic (the EC-206 scheme.display
            // / gameText drop). Conditional spread (never a `finalBlowPending: undefined`
            // literal) satisfies exactOptionalPropertyTypes.
            ...(uiState.mastermind.finalBlowPending !== undefined
                ? { finalBlowPending: uiState.mastermind.finalBlowPending }
                : {}),
            // why: WP-750 / D-24574 — the projected Mastermind fight cost is public
            // shared-board information (every player sees the Dark Portal and the
            // printed cost), passed through for every audience. Optional, so TypeScript
            // does not flag its omission; without this line the client silently falls
            // back to the printed cost and the Portals Mastermind Fight button goes dead
            // again (the EC-206 drop). Conditional spread, never a
            // `fightCost: undefined` literal (exactOptionalPropertyTypes).
            ...(uiState.mastermind.fightCost !== undefined
                ? { fightCost: uiState.mastermind.fightCost }
                : {}),
            // why: WP-757 / D-24587 — "the Mastermind is haunting" is public shared-board
            // information for every audience; optional, so it MUST be passed through here or
            // it is silently dropped (the EC-206 failure mode). Omit-when-absent.
            ...(uiState.mastermind.isHaunting === true ? { isHaunting: true } : {}),
        },
        scheme: {
            id: uiState.scheme.id,
            twistCount: uiState.scheme.twistCount,
            twistPile: deepCopyDisplayEntries(uiState.scheme.twistPile),
            // why: EC-206 — the scheme's display (card art) and its twist /
            // win-condition text are public shared-board information and must
            // survive this whitelist. Both are optional in UISchemeState, so
            // TypeScript does not flag their omission; without these pass-throughs
            // the scheme tile renders blank art + name-only fallback and the
            // CardReaderModal shows "No rules text available for this card" for
            // every match. Conditional spreads satisfy exactOptionalPropertyTypes;
            // display is shallow-copied and gameText is a fresh array copy to
            // prevent aliasing with the input UIState.
            ...(uiState.scheme.display !== undefined
                ? { display: { ...uiState.scheme.display } }
                : {}),
            ...(uiState.scheme.gameText !== undefined
                ? { gameText: [...uiState.scheme.gameText] }
                : {}),
            // why: WP-728 / D-24549 — the Dark-Portal descriptor is public shared-board
            // (portal locations are public, like the scheme display / gameText above);
            // it must survive this whitelist or the board overlay (WP-727) renders
            // nothing — the EC-206 drop-at-filter mode. citySpaceIndices is copied to a
            // fresh array to avoid aliasing with the input UIState.
            ...(uiState.scheme.darkPortals !== undefined
                ? {
                    darkPortals: {
                        onMastermind: uiState.scheme.darkPortals.onMastermind,
                        mastermindAttackBonus: uiState.scheme.darkPortals.mastermindAttackBonus,
                        citySpaceIndices: [...uiState.scheme.darkPortals.citySpaceIndices],
                        citySpaceAttackBonus: uiState.scheme.darkPortals.citySpaceAttackBonus,
                    },
                }
                : {}),
            // why: WP-830 / D-24763 — the Enigma Code is PER-AUDIENCE: a card's display / colors survive
            // only for a viewer who has looked at it, or for everyone once a right guess revealed the
            // code. Every viewer keeps the card count and who has peeked (the peek log is public).
            ...(uiState.scheme.enigmaCode !== undefined
                ? { enigmaCode: filterEnigmaCodeForAudience(uiState.scheme.enigmaCode, audience) }
                : {}),
        },
        economy,
        log: [...uiState.log],
        // why: WP-200 — mirror the `log` filter clone. Spread copy prevents
        // the audience-filtered UIState from aliasing the input UIState's
        // notableEvents array; per-entry payloads are plain JSON objects so
        // top-level copy is sufficient (mirrors `log`).
        notableEvents: [...uiState.notableEvents],
        // why: WP-214 — villainAttachedHeroes is public (visible to all players);
        // per-entry spread copy prevents aliasing with the input UIState
        villainAttachedHeroes: buildVillainAttachedHeroesFilterCopy(uiState.villainAttachedHeroes),
        // why: progress counters are public (no redaction needed) — passed through
        // unchanged via fresh object copy to avoid aliasing with the input UIState.
        // Forced cascade from WP-067 making `progress` a required UIState field.
        progress: { ...uiState.progress },
        // why: WP-606 / D-24417 — the decks object is public and villainDeckComposition
        // rides it. A bare `{ ...uiState.decks }` would shallow-ALIAS the composition
        // array, so it is fresh-copied via a conditional spread (never a
        // `villainDeckComposition: undefined` literal, never a bare `[...maybe-undefined]`)
        // — exactOptionalPropertyTypes-safe, the matchCardImageUrls precedent. The
        // count fields spread as before; the composition is already order-stripped.
        decks: {
            ...uiState.decks,
            ...(uiState.decks.villainDeckComposition !== undefined
                ? { villainDeckComposition: [...uiState.decks.villainDeckComposition] }
                : {}),
        },
        // why: WP-648 follow-on — the piles object is public and the Officer card
        // face (officerDisplay) rides it. A bare `{ ...uiState.piles }` would
        // shallow-ALIAS the officerDisplay object across the full and filtered
        // UIState, so it is fresh-copied via a conditional spread (never an
        // `officerDisplay: undefined` literal) — exactOptionalPropertyTypes-safe,
        // the villainDeckComposition / matchCardImageUrls precedent. The count
        // fields spread as before; officerDisplay is public and unredacted.
        piles: {
            ...uiState.piles,
            ...(uiState.piles.officerDisplay !== undefined
                ? { officerDisplay: { ...uiState.piles.officerDisplay } }
                : {}),
            // why: WP-842 / D-24776 — the Sidekick card face is public too and fresh-copied the
            // same way, so the filtered UIState never aliases the full one's object.
            ...(uiState.piles.sidekickDisplay !== undefined
                ? { sidekickDisplay: { ...uiState.piles.sidekickDisplay } }
                : {}),
        },
        koPile: deepCopyKoPile(uiState.koPile),
    };
    // why: WP-410 / D-24222 — the match card-image manifest passes through PUBLIC
    // and value-identical for every audience (player and spectator). It is
    // information-safe: which card *designs* a match contains is public from the
    // composition; the flat deduped URL set carries no face-down deck order and no
    // per-player hidden state, so no redaction applies. A fresh array copy prevents
    // aliasing with the input UIState; conditional assignment (never a `matchCardImageUrls:
    // undefined` literal) satisfies exactOptionalPropertyTypes. buildUIState always
    // populates the field, so in practice this always fires. Without this pass-through
    // the field is dropped at this whitelist boundary and the client never warms.
    if (uiState.matchCardImageUrls !== undefined) {
        result.matchCardImageUrls = [...uiState.matchCardImageUrls];
    }
    // why: WP-664 / D-24475 — the Transform side deck (G.transformDeck, WP-657) is
    // PUBLIC face-up shared-board data — the second-form cards are laid out face-up
    // in the physical game — so it passes through value-identical for EVERY audience
    // (owner, opponent, spectator); no redaction applies. It is optional in UIState,
    // so TypeScript does not flag its omission — an optional field populated in
    // buildUIState but missing this pass-through is silently dropped at the whitelist
    // (the EC-206 / D-12803 Board-Visible Field Rule failure), so the client would
    // never see the transform deck. deepCopyDisplayEntries does the per-entry shallow
    // copy (no aliasing); conditional assignment (never a `transformDeck: undefined`
    // literal) satisfies exactOptionalPropertyTypes; buildUIState always populates it,
    // so in practice this always fires.
    if (uiState.transformDeck !== undefined) {
        result.transformDeck = deepCopyDisplayEntries(uiState.transformDeck);
    }
    // why: WP-789 / D-24637 — the revealed cards behind `heroEffectResolved` overlay
    // events are PUBLIC (a revealed card is shown at the table; the event itself is
    // public), so the entries pass through for EVERY audience, copied entry-by-entry
    // like transformDeck. Optional, so a missing pass-through would be silently dropped
    // at this whitelist (the Board-Visible Field Rule) and a miss would show a raw id.
    if (uiState.notableEventCards !== undefined) {
        result.notableEventCards = deepCopyDisplayEntries(uiState.notableEventCards);
    }
    if (uiState.gameOver !== undefined) {
        result.gameOver = { ...uiState.gameOver };
    }
    // why: WP-655 / D-24466 — finalTurn is PUBLIC shared-board information (the
    // deck-remaining counts + reason) and must survive this whitelist, exactly
    // like gameOver / matchCardImageUrls above. It is optional in UIState, so
    // TypeScript does not flag its omission — and WP-367 populated it in
    // buildUIState but never added this pass-through, so the field was silently
    // dropped at the audience filter and the final-turn banner (WP-368 / WP-654)
    // never reached the client (the D-12803 / EC-206 Board-Visible Field Rule
    // failure mode). A fresh object copy prevents aliasing with the input
    // UIState; conditional assignment (never a `finalTurn: undefined` literal)
    // satisfies exactOptionalPropertyTypes. buildUIState already omits the field
    // once gameOver is set, so present-here means the match is in its final turn.
    if (uiState.finalTurn !== undefined) {
        result.finalTurn = { ...uiState.finalTurn };
    }
    // why: D-22202 — pendingHeroChoice passes through for all audiences without
    // redaction. The revealed card is face-up at the physical table; all players
    // and spectators see it. Conditional-assignment pattern matches gameOver above
    // — do NOT assign `pendingHeroChoice: undefined` on the result literal
    // (exactOptionalPropertyTypes).
    if (uiState.pendingHeroChoice !== undefined) {
        result.pendingHeroChoice = {
            ...uiState.pendingHeroChoice,
            display: { ...uiState.pendingHeroChoice.display },
        };
    }
    // why: D-24011 — pendingKoHeroChoice is redacted for EVERY audience except
    // the choosing player. Unlike the public pendingHeroChoice (the revealed
    // card is face-up on the table), the KO eligible list carries the chooser's
    // HAND identities; passing it through to opponents or spectators would leak
    // the hand. Present only when audience is a player whose playerId equals the
    // chooser's playerID; omitted (conditional assignment, never an `undefined`
    // literal) for opponents AND spectators. Per-entry display spread prevents
    // aliasing with the input UIState.
    if (uiState.pendingKoHeroChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingKoHeroChoice.playerID) {
        const eligibleCopy = [];
        for (const entry of uiState.pendingKoHeroChoice.eligible) {
            eligibleCopy.push({
                zone: entry.zone,
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingKoHeroChoice = {
            choiceType: uiState.pendingKoHeroChoice.choiceType,
            playerID: uiState.pendingKoHeroChoice.playerID,
            eligible: eligibleCopy,
            remaining: uiState.pendingKoHeroChoice.remaining,
        };
    }
    // why: WP-470 / D-24282 — the pending Doombot scry-KO choice is redacted for EVERY
    // audience except the choosing player. The revealedCards are the top of the chooser's
    // OWN deck (their next draws) — leaking them to opponents or spectators would reveal
    // hidden deck-order information. Present only when the audience is a player whose
    // playerId equals the chooser's playerID; omitted (conditional assignment, never an
    // `undefined` literal) for opponents AND spectators. Per-entry display spread prevents
    // aliasing with the input UIState.
    if (uiState.pendingScryKoChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingScryKoChoice.playerID) {
        const revealedCopy = [];
        for (const entry of uiState.pendingScryKoChoice.revealedCards) {
            revealedCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingScryKoChoice = {
            choiceType: uiState.pendingScryKoChoice.choiceType,
            playerID: uiState.pendingScryKoChoice.playerID,
            revealedCards: revealedCopy,
        };
    }
    // why: WP-603 / D-24413 — the pending Melter Fight KO/keep choice is redacted for
    // EVERY audience except the fighting (choosing) player. Its revealedTops are the
    // tops of players' OWN decks (hidden next-draw information) — leaking them to
    // opponents or spectators would reveal deck-order. Present only when the audience is
    // a player whose playerId equals the chooser's playerID; omitted (conditional
    // assignment, never an `undefined` literal) for opponents AND spectators. Rebuilt
    // field-by-field; per-entry display spread prevents aliasing with the input UIState.
    if (uiState.pendingMelterKoChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingMelterKoChoice.playerID) {
        const revealedTopsCopy = [];
        for (const entry of uiState.pendingMelterKoChoice.revealedTops) {
            revealedTopsCopy.push({
                ownerPlayerID: entry.ownerPlayerID,
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingMelterKoChoice = {
            choiceType: uiState.pendingMelterKoChoice.choiceType,
            playerID: uiState.pendingMelterKoChoice.playerID,
            revealedTops: revealedTopsCopy,
        };
    }
    // why: WP-702 / D-24521 — the pending reveal-top discard-or-keep choice is redacted for
    // EVERY audience except the choosing (active) player. Its revealedTops are the tops of
    // players' OWN decks (their next draws) — leaking them to opponents/spectators would reveal
    // deck-order. Present only when the audience is a player whose playerId equals the chooser's
    // playerID; omitted (conditional assignment, never an `undefined` literal) for opponents AND
    // spectators. Rebuilt field-by-field; per-entry display spread prevents aliasing with input.
    if (uiState.pendingRevealTopDispose !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingRevealTopDispose.playerID) {
        const revealedTopsCopy = [];
        for (const entry of uiState.pendingRevealTopDispose.revealedTops) {
            revealedTopsCopy.push({
                ownerPlayerID: entry.ownerPlayerID,
                cardId: entry.cardId,
                display: { ...entry.display },
                // why: D-24558 — pass the KO unlock through the whitelist (the five-step field
                // contract); dropping it here would silently hide the covert KO button.
                ...(entry.isKoAllowed === true ? { isKoAllowed: true } : {}),
                // why: WP-754 / D-24581 — pass the discard lock through the whitelist; dropping it here
                // would offer a Discard button the engine rejects on a KO-or-keep entry.
                ...(entry.isDiscardAllowed === false ? { isDiscardAllowed: false } : {}),
            });
        }
        result.pendingRevealTopDispose = {
            choiceType: uiState.pendingRevealTopDispose.choiceType,
            playerID: uiState.pendingRevealTopDispose.playerID,
            revealedTops: revealedTopsCopy,
        };
    }
    // why: WP-695 / D-24512 — the pending Ruthless Dictator scry-3 choice is redacted for
    // EVERY audience except the choosing (defeating) player. Its revealedCards are the top
    // of the chooser's OWN deck (their next draws) — leaking them to opponents/spectators
    // would reveal deck-order. Present only when the audience is a player whose playerId
    // equals the chooser's playerID; omitted (conditional assignment, never an `undefined`
    // literal) for opponents AND spectators. Rebuilt field-by-field; per-entry display
    // spread prevents aliasing with the input UIState.
    if (uiState.pendingRuthlessDictatorChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingRuthlessDictatorChoice.playerID) {
        const revealedCopy = [];
        for (const entry of uiState.pendingRuthlessDictatorChoice.revealedCards) {
            revealedCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingRuthlessDictatorChoice = {
            choiceType: uiState.pendingRuthlessDictatorChoice.choiceType,
            playerID: uiState.pendingRuthlessDictatorChoice.playerID,
            revealedCards: revealedCopy,
            availableDispositions: [...uiState.pendingRuthlessDictatorChoice.availableDispositions],
        };
    }
    // why: WP-753 / D-24580 — the pending reveal-three assignment is redacted for EVERY audience
    // except the choosing (active) player, the Ruthless Dictator audience: its revealedCards are
    // the top of the chooser's OWN deck (their next draws). Present only when the audience is a
    // player whose playerId equals the chooser's playerID; omitted (conditional assignment, never
    // an `undefined` literal) for opponents AND spectators. Rebuilt field-by-field; per-entry
    // display spread prevents aliasing with the input UIState.
    if (uiState.pendingRevealThreeAssign !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingRevealThreeAssign.playerID) {
        const revealedThreeCopy = [];
        for (const entry of uiState.pendingRevealThreeAssign.revealedCards) {
            revealedThreeCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingRevealThreeAssign = {
            choiceType: uiState.pendingRevealThreeAssign.choiceType,
            playerID: uiState.pendingRevealThreeAssign.playerID,
            sourceCard: {
                cardId: uiState.pendingRevealThreeAssign.sourceCard.cardId,
                display: { ...uiState.pendingRevealThreeAssign.sourceCard.display },
            },
            revealedCards: revealedThreeCopy,
            availableDispositions: [...uiState.pendingRevealThreeAssign.availableDispositions],
            remainingRepeats: uiState.pendingRevealThreeAssign.remainingRepeats,
        };
    }
    // why: WP-695 / D-24512 — the pending Electromagnetic Bubble X-Men pick is redacted for
    // EVERY audience except the choosing (defeating) player (kept owner-only for consistency
    // with the other pending picks — the decision is the chooser's alone). Present only when
    // the audience is a player whose playerId equals the chooser's playerID; omitted for
    // opponents AND spectators. Rebuilt field-by-field; per-entry display spread prevents
    // aliasing with the input UIState.
    if (uiState.pendingElectromagneticBubbleChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingElectromagneticBubbleChoice.playerID) {
        const eligibleCopy = [];
        for (const entry of uiState.pendingElectromagneticBubbleChoice.eligibleCards) {
            eligibleCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingElectromagneticBubbleChoice = {
            choiceType: uiState.pendingElectromagneticBubbleChoice.choiceType,
            playerID: uiState.pendingElectromagneticBubbleChoice.playerID,
            eligibleCards: eligibleCopy,
        };
    }
    // why: WP-476 / D-24284 — the pending discard-to-limit choice is redacted for EVERY
    // audience except the choosing player. Its `hand` carries the chooser's PRIVATE hand
    // identities — the very cards they must choose among — so passing it through to
    // opponents or spectators would leak the hand (D-24011). Present only when the
    // audience is a player whose playerId equals the chooser's playerID; omitted
    // (conditional assignment, never an `undefined` literal) for opponents AND
    // spectators. Per-entry display spread prevents aliasing with the input UIState.
    if (uiState.pendingDiscardChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingDiscardChoice.playerID) {
        const handCopy = [];
        for (const entry of uiState.pendingDiscardChoice.hand) {
            handCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingDiscardChoice = {
            choiceType: uiState.pendingDiscardChoice.choiceType,
            playerID: uiState.pendingDiscardChoice.playerID,
            limit: uiState.pendingDiscardChoice.limit,
            hand: handCopy,
        };
    }
    // why: WP-538 / D-24347 — the put-cards-on-deck hand carries the chooser's private card
    // identities, private to the chooser. Present only when the audience is a player whose
    // playerId equals the chooser's playerID; omitted (conditional assignment, never an
    // `undefined` literal) for opponents AND spectators. Per-entry display spread prevents
    // aliasing with the input UIState — mirroring pendingDiscardChoice.
    if (uiState.pendingPutCardsOnDeckChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingPutCardsOnDeckChoice.playerID) {
        const handCopy = [];
        for (const entry of uiState.pendingPutCardsOnDeckChoice.hand) {
            handCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingPutCardsOnDeckChoice = {
            choiceType: uiState.pendingPutCardsOnDeckChoice.choiceType,
            playerID: uiState.pendingPutCardsOnDeckChoice.playerID,
            count: uiState.pendingPutCardsOnDeckChoice.count,
            hand: handCopy,
        };
    }
    // why: WP-479 / D-24286 — the reorder remainder is the top of the chooser's own deck
    // (their next draws), private to the chooser. Present only when the audience is a player
    // whose playerId equals the chooser's playerID; omitted (conditional assignment, never an
    // `undefined` literal) for opponents AND spectators. Per-entry display spread prevents
    // aliasing with the input UIState.
    if (uiState.pendingReorderChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingReorderChoice.playerID) {
        const cardsCopy = [];
        for (const entry of uiState.pendingReorderChoice.cards) {
            cardsCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingReorderChoice = {
            choiceType: uiState.pendingReorderChoice.choiceType,
            playerID: uiState.pendingReorderChoice.playerID,
            cards: cardsCopy,
        };
    }
    // why: WP-486 / D-24291 — a pending defeat-with-a-Bystander choice is the chooser's
    // pending decision (the board is frozen for everyone, but only the chooser resolves
    // it). Present only when the audience is the choosing player; omitted (conditional
    // assignment, never an `undefined` literal) for opponents AND spectators. Per-target
    // display spread prevents aliasing with the input UIState.
    if (uiState.pendingDefeatChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingDefeatChoice.playerID) {
        const targetsCopy = [];
        for (const entry of uiState.pendingDefeatChoice.targets) {
            targetsCopy.push({
                kind: entry.kind,
                // why: cityIndex present only for a villain target (exactOptionalPropertyTypes).
                ...(entry.cityIndex !== undefined ? { cityIndex: entry.cityIndex } : {}),
                display: { ...entry.display },
            });
        }
        result.pendingDefeatChoice = {
            choiceType: uiState.pendingDefeatChoice.choiceType,
            playerID: uiState.pendingDefeatChoice.playerID,
            targets: targetsCopy,
        };
    }
    // why: WP-693 / D-24510 — the KO-from-discard choice is the chooser's pending decision
    // (the board is frozen for everyone, but only the chooser resolves it). Present only when
    // the audience is the choosing player; omitted (conditional assignment, never an
    // `undefined` literal) for opponents AND spectators. Per-entry display spread prevents
    // aliasing with the input UIState — mirroring pendingDefeatChoice / pendingPutCardsOnDeckChoice.
    if (uiState.pendingKoDiscardChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingKoDiscardChoice.playerID) {
        const discardCopy = [];
        for (const entry of uiState.pendingKoDiscardChoice.discard) {
            discardCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingKoDiscardChoice = {
            choiceType: uiState.pendingKoDiscardChoice.choiceType,
            playerID: uiState.pendingKoDiscardChoice.playerID,
            maxCount: uiState.pendingKoDiscardChoice.maxCount,
            // why: D-24783 — pass the grey filter through to the chooser (the whitelist drops any
            // field it does not name); omitted on an unfiltered entry.
            ...(uiState.pendingKoDiscardChoice.greyFilter !== undefined
                ? { greyFilter: uiState.pendingKoDiscardChoice.greyFilter }
                : {}),
            // why: WP-850 / D-24786 — pass the "limited to the cards just discarded" flag through to the
            // chooser (the whitelist drops any field it does not name); omitted on every other entry.
            ...(uiState.pendingKoDiscardChoice.isLimitedToDiscardedThisWay === true
                ? { isLimitedToDiscardedThisWay: true }
                : {}),
            discard: discardCopy,
        };
    }
    // why: D-24020 — hand/discard are private to the chooser. pendingOptionalKoReward
    // is redacted for EVERY audience except the choosing player (the D-24011
    // hand-privacy analog). Its eligibleHand and eligibleDiscard lists carry the
    // chooser's PRIVATE hand and discard identities; passing them through to
    // opponents or spectators would leak both zones. Present only when the audience
    // is a player whose playerId equals the chooser's playerID; omitted (conditional
    // assignment, never an `undefined` literal) for opponents AND spectators.
    // Per-entry display spread prevents aliasing with the input UIState.
    if (uiState.pendingOptionalKoReward !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingOptionalKoReward.playerID) {
        const eligibleHandCopy = [];
        for (const entry of uiState.pendingOptionalKoReward.eligibleHand) {
            eligibleHandCopy.push({
                zone: entry.zone,
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        const eligibleDiscardCopy = [];
        for (const entry of uiState.pendingOptionalKoReward.eligibleDiscard) {
            eligibleDiscardCopy.push({
                zone: entry.zone,
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        // why: D-24442 — the in-play eligible list carries the chooser's private
        // played-this-turn cards; pass it through with a per-entry display spread on
        // the SAME owner-only branch as hand/discard. Missing this drops the field at
        // the whitelist and the client renders no in-play KO options (the recurring
        // filter-drop failure mode).
        const eligibleInPlayCopy = [];
        for (const entry of uiState.pendingOptionalKoReward.eligibleInPlay) {
            eligibleInPlayCopy.push({
                zone: entry.zone,
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingOptionalKoReward = {
            playerID: uiState.pendingOptionalKoReward.playerID,
            rewardLabel: uiState.pendingOptionalKoReward.rewardLabel,
            eligibleHand: eligibleHandCopy,
            eligibleDiscard: eligibleDiscardCopy,
            eligibleInPlay: eligibleInPlayCopy,
        };
    }
    // why: WP-676 / D-24492 — the pending Smash discard-for-attack choice is private to the
    // chooser. Its eligibleHand list carries the chooser's PRIVATE hand identities, so it is
    // redacted for EVERY audience except the choosing player (the D-24011 hand-privacy analog).
    // Present only when the audience is a player whose playerId equals the chooser's playerID;
    // omitted (conditional assignment, never an `undefined` literal) for opponents AND
    // spectators. The assignment rebuilds each hand entry field-by-field (the audience-filter
    // whitelist pattern) with a per-entry display spread to prevent aliasing with the input
    // UIState. Missing this pass-through drops the field at the whitelist and the game freezes
    // with no prompt (the recurring filter-drop failure mode).
    if (uiState.pendingSmashDiscard !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingSmashDiscard.playerID) {
        const eligibleHandCopy = [];
        for (const entry of uiState.pendingSmashDiscard.eligibleHand) {
            eligibleHandCopy.push({
                zone: entry.zone,
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingSmashDiscard = {
            playerID: uiState.pendingSmashDiscard.playerID,
            magnitude: uiState.pendingSmashDiscard.magnitude,
            // why: WP-754 / D-24581 — pass the draw reward through the whitelist; dropping it here
            // would label a discard-to-draw choice as Smash (+N attack).
            ...(uiState.pendingSmashDiscard.reward === 'draw' ? { reward: 'draw' } : {}),
            eligibleHand: eligibleHandCopy,
        };
    }
    // why: WP-700 / D-24519 / D-24011 analog — the pending put-a-hand-card-on-deck-top choice is
    // private to the chooser (only they may resolve it, and eligibleHand carries their private
    // hand identities). Present only when the audience is a player whose playerId equals the
    // chooser's playerID; omitted (conditional assignment, never an `undefined` literal) for
    // opponents AND spectators. The assignment rebuilds each hand entry field-by-field (the
    // audience-filter whitelist pattern) with a per-entry display spread to prevent aliasing with
    // the input UIState. Missing this pass-through drops the field at the whitelist and the game
    // freezes with no prompt (the recurring filter-drop failure mode).
    if (uiState.pendingPutHandOnDeckTop !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingPutHandOnDeckTop.playerID) {
        const eligibleHandCopy = [];
        for (const entry of uiState.pendingPutHandOnDeckTop.eligibleHand) {
            eligibleHandCopy.push({
                zone: entry.zone,
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingPutHandOnDeckTop = {
            playerID: uiState.pendingPutHandOnDeckTop.playerID,
            eligibleHand: eligibleHandCopy,
        };
    }
    // why: WP-681 / D-24498 / D-24011 analog — the pending Do-Over choice is private to the
    // chooser (only they may resolve it). Present only when the audience is a player whose
    // playerId equals the chooser's playerID; omitted (conditional assignment, never an
    // `undefined` literal) for opponents AND spectators — mirroring the pendingDrawOrEmpowered
    // posture. A flat object (binary accept/decline, no eligible-card list); handSize is a count,
    // not a card identity, so it carries no private hand contents. Missing this pass-through
    // drops the field at the whitelist and the game freezes with no prompt.
    if (uiState.pendingDoOver !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingDoOver.playerID) {
        result.pendingDoOver = {
            playerID: uiState.pendingDoOver.playerID,
            handSize: uiState.pendingDoOver.handSize,
            drawCount: uiState.pendingDoOver.drawCount,
        };
    }
    // why: D-24071 / D-24011 analog — the pending draw-or-empowered choice is private to
    // the chooser. Redacted for EVERY audience except the choosing player; present only
    // when the audience is a player whose playerId equals the chooser's playerID; omitted
    // (conditional assignment, never an `undefined` literal) for opponents AND spectators.
    // No eligible-card list to copy (binary choice), so the assignment is a flat object —
    // empoweredLabel is a derived display string carrying no private identity.
    if (uiState.pendingDrawOrEmpowered !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingDrawOrEmpowered.playerID) {
        result.pendingDrawOrEmpowered = {
            playerID: uiState.pendingDrawOrEmpowered.playerID,
            empoweredLabel: uiState.pendingDrawOrEmpowered.empoweredLabel,
        };
    }
    // why: WP-719 / D-24541 — the pending Covering Fire choose-one (Hawkeye) is private to the
    // chooser (only the active player may resolve it). Present only when the audience is the
    // choosing player; omitted (conditional assignment, never an `undefined` literal) for
    // opponents AND spectators — mirroring the pendingDrawOrEmpowered posture. otherPlayerCount is
    // a derived count carrying no private identity. A field that reaches build but not this
    // whitelist is dropped at the filter (the shipped board-visible-field failure mode).
    if (uiState.pendingCoveringFireChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingCoveringFireChoice.playerID) {
        result.pendingCoveringFireChoice = {
            playerID: uiState.pendingCoveringFireChoice.playerID,
            otherPlayerCount: uiState.pendingCoveringFireChoice.otherPlayerCount,
        };
    }
    // why: WP-795 / D-24664 — the pending Spinning Cyclone move-a-Villain choice is private to the
    // chooser (only the active player may resolve it). Present only when the audience is the
    // choosing player; omitted (conditional assignment, never an `undefined` literal) for opponents
    // AND spectators — mirroring the pendingCoveringFireChoice posture above. villainCityIndices is
    // copied into a fresh array (the split-face fresh-object precedent below) so the projection
    // never aliases the built state. A field that reaches build but not this whitelist is dropped at
    // the filter (the shipped board-visible-field failure mode) — the prompt would never render.
    if (uiState.pendingMoveVillainChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingMoveVillainChoice.playerID) {
        result.pendingMoveVillainChoice = {
            playerID: uiState.pendingMoveVillainChoice.playerID,
            villainCityIndices: [...uiState.pendingMoveVillainChoice.villainCityIndices],
        };
    }
    // why: WP-724 / D-24546 — the pending split-face "choose a side" picker is private to the chooser
    // (only the active player may resolve it). Present only when the audience is the choosing player;
    // omitted (conditional assignment, never an `undefined` literal) for opponents AND spectators —
    // mirroring the pendingCoveringFireChoice posture above. Both faces carry only public card display
    // + economy (no private identity). A field that reaches build but not this whitelist is dropped at
    // the filter (the shipped board-visible-field failure mode) — fresh face objects avoid aliasing.
    if (uiState.pendingSplitFaceChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingSplitFaceChoice.playerID) {
        result.pendingSplitFaceChoice = {
            playerID: uiState.pendingSplitFaceChoice.playerID,
            faceA: { ...uiState.pendingSplitFaceChoice.faceA },
            faceB: { ...uiState.pendingSplitFaceChoice.faceB },
            leftFace: uiState.pendingSplitFaceChoice.leftFace,
        };
    }
    // why: WP-675 / D-24490 — the pending count-scaled choose-one (vnom Symbiotic Adaptation)
    // is private to the chooser (only they may resolve it). Present only when the audience is a
    // player whose playerId equals the chooser's playerID; omitted (conditional assignment, never
    // an `undefined` literal) for opponents AND spectators — mirroring the pendingDrawOrEmpowered
    // posture. The options carry only resolved counts + resource labels — no private identity — so
    // the assignment rebuilds each option field-by-field (the audience-filter whitelist pattern).
    if (uiState.pendingCountScaledChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingCountScaledChoice.playerID) {
        result.pendingCountScaledChoice = {
            playerID: uiState.pendingCountScaledChoice.playerID,
            // why: WP-679 / D-24495 — rebuild each option field-by-field (whitelist), carrying the
            // new `kind` + `label` and the (optional) count-scaled fields for both option variants.
            options: uiState.pendingCountScaledChoice.options.map((option) => ({
                kind: option.kind,
                label: option.label,
                ...(option.resource !== undefined ? { resource: option.resource } : {}),
                ...(option.perUnit !== undefined ? { perUnit: option.perUnit } : {}),
                ...(option.count !== undefined ? { count: option.count } : {}),
                ...(option.total !== undefined ? { total: option.total } : {}),
            })),
        };
    }
    // why: WP-678 / D-24494 — the pending Undercover target pick is private to the chooser (only
    // they may send a Hero Undercover). Present only when the audience is the chooser; omitted
    // (conditional assignment, never an `undefined` literal) for opponents AND spectators. The
    // eligible ext_ids are the chooser's own hand Heroes — rebuilt field-by-field (whitelist pattern).
    if (uiState.pendingUndercoverChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingUndercoverChoice.playerID) {
        result.pendingUndercoverChoice = {
            playerID: uiState.pendingUndercoverChoice.playerID,
            eligibleTargets: [...uiState.pendingUndercoverChoice.eligibleTargets],
        };
    }
    // why: WP-684 / D-24501 — PER-SEAT redaction of the non-active/multi-seat pending choice.
    // Emitted ONLY to an audience that is an addressed, still-outstanding seat (the
    // outstandingSeats membership test covers both "addressed" and "not yet submitted"), and
    // rebuilt to carry ONLY that seat's OWN prompt in seatPrompts — never another seat's
    // options (the per-seat redaction contract). Opponents, spectators, and already-submitted
    // seats get it omitted (conditional assignment, never an `undefined` literal).
    // addressedSeats / outstandingSeats are seat-id liveness info (no private data), copied as-is.
    if (uiState.pendingSeatChoice !== undefined &&
        audience.kind === 'player' &&
        uiState.pendingSeatChoice.outstandingSeats.includes(audience.playerId)) {
        const seatChoice = uiState.pendingSeatChoice;
        const ownPrompt = seatChoice.seatPrompts[audience.playerId];
        result.pendingSeatChoice = {
            kind: seatChoice.kind,
            addressedSeats: [...seatChoice.addressedSeats],
            outstandingSeats: [...seatChoice.outstandingSeats],
            seatPrompts: {
                [audience.playerId]: {
                    options: (ownPrompt?.options ?? []).map((option) => ({ label: option.label })),
                },
            },
        };
    }
    // why: WP-663 / D-24474 — the pending play-top-Villain-Deck choice (Shadowed Thoughts)
    // is private to the chooser (only they may resolve it). Redacted for EVERY audience
    // except the choosing player; present only when the audience is a player whose playerId
    // equals the chooser's playerID; omitted (conditional assignment, never an `undefined`
    // literal) for opponents AND spectators — mirroring the pendingDrawOrEmpowered posture.
    // Binary choice, no eligible-card list; attackReward is the derived +N Attack magnitude.
    if (uiState.pendingPlayVillainTop !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingPlayVillainTop.playerID) {
        result.pendingPlayVillainTop = {
            playerID: uiState.pendingPlayVillainTop.playerID,
            attackReward: uiState.pendingPlayVillainTop.attackReward,
        };
    }
    // why: D-24099 — the pending victory-pile villain-pick is scoped to the chooser (only
    // they may resolve it). Redacted for EVERY audience except the choosing player;
    // present only when the audience is a player whose playerId equals the chooser's
    // playerID; omitted (conditional assignment, never an `undefined` literal) for
    // opponents AND spectators — mirroring the pendingDrawOrEmpowered posture. The
    // eligibleVillains list is victory-pile (public) data, but the pending-choice state
    // itself is chooser-scoped; per-entry display spread prevents aliasing with the input.
    if (uiState.pendingVictoryPileCardPick !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingVictoryPileCardPick.playerID) {
        const eligibleVillainsCopy = [];
        for (const entry of uiState.pendingVictoryPileCardPick.eligibleVillains) {
            eligibleVillainsCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
                attackValue: entry.attackValue,
            });
        }
        result.pendingVictoryPileCardPick = {
            playerID: uiState.pendingVictoryPileCardPick.playerID,
            eligibleVillains: eligibleVillainsCopy,
        };
    }
    // why: the pending optional-put-bottom-hq choice is scoped to the chooser (only they
    // may resolve it). Redacted for EVERY audience except the choosing player; present
    // only when the audience is a player whose playerId equals the chooser's playerID;
    // omitted (conditional assignment, never an `undefined` literal) for opponents AND
    // spectators — mirroring the pendingVictoryPileCardPick posture. The eligibleHqCards
    // list is HQ (public) data, but the pending-choice state itself is chooser-scoped;
    // per-entry display spread prevents aliasing with the input.
    if (uiState.pendingOptionalPutBottomHQ !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingOptionalPutBottomHQ.playerID) {
        const eligibleHqCardsCopy = [];
        for (const entry of uiState.pendingOptionalPutBottomHQ.eligibleHqCards) {
            eligibleHqCardsCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingOptionalPutBottomHQ = {
            playerID: uiState.pendingOptionalPutBottomHQ.playerID,
            eligibleHqCards: eligibleHqCardsCopy,
            // why: D-24133 — carry the mandatory flag through redaction so the chooser's
            // client hides Decline (omit-when-false preserves the optional form's shape).
            ...(uiState.pendingOptionalPutBottomHQ.mandatory === true ? { mandatory: true } : {}),
        };
    }
    // why: D-24132 — the pending put-any-number-bottom-hq choice (the MULTI-select sibling) is
    // scoped to the chooser (only they may resolve it). Redacted for EVERY audience except the
    // choosing player; present only when the audience is a player whose playerId equals the
    // chooser's playerID; omitted (conditional assignment, never an `undefined` literal) for
    // opponents AND spectators — mirroring the pendingOptionalPutBottomHQ posture. The
    // eligibleHqCards list is HQ (public) data, but the pending-choice state itself is chooser-
    // scoped; per-entry display spread prevents aliasing with the input.
    if (uiState.pendingPutAnyNumberBottomHQ !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingPutAnyNumberBottomHQ.playerID) {
        const eligibleHqCardsCopy = [];
        for (const entry of uiState.pendingPutAnyNumberBottomHQ.eligibleHqCards) {
            eligibleHqCardsCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingPutAnyNumberBottomHQ = {
            playerID: uiState.pendingPutAnyNumberBottomHQ.playerID,
            eligibleHqCards: eligibleHqCardsCopy,
        };
    }
    // why: D-24139 — the pending return-zero-cost-discard choice is scoped to the chooser
    // (only they may resolve it). Redacted for EVERY audience except the choosing player;
    // present only when the audience is a player whose playerId equals the chooser's
    // playerID; omitted (conditional assignment, never an `undefined` literal) for
    // opponents AND spectators — mirroring the pendingOptionalPutBottomHQ posture. The
    // eligibleDiscardCards list carries the chooser's own discard cards; per-entry display
    // spread prevents aliasing with the input.
    if (uiState.pendingReturnZeroCostDiscard !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingReturnZeroCostDiscard.playerID) {
        const eligibleDiscardCardsCopy = [];
        for (const entry of uiState.pendingReturnZeroCostDiscard.eligibleDiscardCards) {
            eligibleDiscardCardsCopy.push({
                zone: entry.zone,
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingReturnZeroCostDiscard = {
            playerID: uiState.pendingReturnZeroCostDiscard.playerID,
            eligibleDiscardCards: eligibleDiscardCardsCopy,
        };
    }
    // why: WP-383 / D-24184 — the pending discard-to-play cost is scoped to the chooser
    // (only they may pay it). Redacted for EVERY audience except the choosing player;
    // present only when the audience is a player whose playerId equals the chooser's
    // playerID; omitted (conditional assignment, never an `undefined` literal) for
    // opponents AND spectators — mirroring pendingReturnZeroCostDiscard. The
    // eligibleDiscardCards list carries the chooser's own hand; per-entry display spread
    // prevents aliasing with the input.
    if (uiState.pendingDiscardToPlay !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingDiscardToPlay.playerID) {
        const eligibleDiscardCardsCopy = [];
        for (const entry of uiState.pendingDiscardToPlay.eligibleDiscardCards) {
            eligibleDiscardCardsCopy.push({
                zone: entry.zone,
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingDiscardToPlay = {
            playerID: uiState.pendingDiscardToPlay.playerID,
            remaining: uiState.pendingDiscardToPlay.remaining,
            eligibleDiscardCards: eligibleDiscardCardsCopy,
        };
    }
    // why: WP-498 / D-24301 — pendingReturnOnDiscard is chooser-only; the OPTIONAL
    // return prompt is projected ONLY to the discarding player, redacted (omitted)
    // for opponents AND spectators — mirroring pendingDiscardToPlay. The
    // eligibleReturnCards list carries the chooser's own just-discarded card;
    // per-entry display spread prevents aliasing with the input.
    if (uiState.pendingReturnOnDiscard !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingReturnOnDiscard.playerID) {
        const eligibleReturnCardsCopy = [];
        for (const entry of uiState.pendingReturnOnDiscard.eligibleReturnCards) {
            eligibleReturnCardsCopy.push({
                zone: entry.zone,
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingReturnOnDiscard = {
            playerID: uiState.pendingReturnOnDiscard.playerID,
            eligibleReturnCards: eligibleReturnCardsCopy,
        };
    }
    // why: WP-532 / D-24343 — pendingGiveHqHeroChoice is chooser-only; the interactive
    // give-HQ-Hero prompt is projected ONLY to the current (fighting) player, redacted
    // (omitted) for opponents AND spectators — mirroring pendingReturnOnDiscard. The
    // eligible list carries PUBLIC HQ Heroes, but only the chooser's prompt is surfaced;
    // per-entry display spread prevents aliasing with the input.
    if (uiState.pendingGiveHqHeroChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingGiveHqHeroChoice.playerID) {
        const eligibleGiveCardsCopy = [];
        for (const entry of uiState.pendingGiveHqHeroChoice.eligible) {
            eligibleGiveCardsCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingGiveHqHeroChoice = {
            choiceType: uiState.pendingGiveHqHeroChoice.choiceType,
            playerID: uiState.pendingGiveHqHeroChoice.playerID,
            eligible: eligibleGiveCardsCopy,
            // why: WP-692 / D-24509 — pass through the optional ("may") flag so the chooser
            // keeps the Decline control (Dark Technology). Five-step field: type + build +
            // THIS filter pass-through + audience test + diagnostics snapshot.
            ...(uiState.pendingGiveHqHeroChoice.optional === true ? { optional: true } : {}),
        };
    }
    // why: WP-535 / D-24345 — pendingCopyPowersChoice is chooser-only; the interactive
    // Copy Powers prompt is projected ONLY to the copying player, redacted (omitted) for
    // opponents AND spectators — mirroring pendingGiveHqHeroChoice. The eligible list carries
    // PUBLIC in-play Heroes, but only the chooser's prompt is surfaced; per-entry display
    // spread prevents aliasing with the input. (Board-Visible Field Rule: without this arm the
    // built field is silently dropped at the filter → a client freeze with no prompt.)
    if (uiState.pendingCopyPowersChoice !== undefined &&
        audience.kind === 'player' &&
        audience.playerId === uiState.pendingCopyPowersChoice.playerID) {
        const eligibleCopyCardsCopy = [];
        for (const entry of uiState.pendingCopyPowersChoice.eligible) {
            eligibleCopyCardsCopy.push({
                cardId: entry.cardId,
                display: { ...entry.display },
            });
        }
        result.pendingCopyPowersChoice = {
            choiceType: uiState.pendingCopyPowersChoice.choiceType,
            playerID: uiState.pendingCopyPowersChoice.playerID,
            eligible: eligibleCopyCardsCopy,
        };
    }
    // why: WP-258 / D-12803 — hollowEffects is PUBLIC card/mechanic data, not
    // hidden info. The filter passes it through value-unchanged for EVERY
    // audience (own-player AND other-player AND spectator) — it redacts /
    // reorders / rewrites / drops NOTHING. A per-record fresh-object copy
    // (aliasing defense, mirroring the notableEvents / victoryCards posture)
    // prevents the audience-filtered UIState from aliasing the input array, while
    // every field value is preserved so both audiences deep-equal the source.
    // Conditional assignment (never a `hollowEffects: undefined` literal) keeps
    // the absent-channel case omitting the field under exactOptionalPropertyTypes.
    if (uiState.hollowEffects !== undefined) {
        const hollowEffectsCopy = [];
        for (const record of uiState.hollowEffects) {
            hollowEffectsCopy.push({
                cardId: record.cardId,
                cardType: record.cardType,
                timing: record.timing,
                mechanic: record.mechanic,
                reason: record.reason,
                turn: record.turn,
            });
        }
        result.hollowEffects = hollowEffectsCopy;
    }
    // why: WP-575 / D-12803 — effectTraces is PUBLIC card/mechanic dispatch data, not
    // hidden info. The filter passes it through value-unchanged for EVERY audience
    // (own-player AND other-player AND spectator) — it redacts / reorders / rewrites /
    // drops NOTHING, exactly like hollowEffects above. A per-record fresh-object copy
    // (aliasing defense, D-11105) — including a fresh `params` object, since `params`
    // is a nested map — prevents the audience-filtered UIState from aliasing the input,
    // while every field value is preserved so every audience deep-equals the source.
    // Conditional assignment (never an `effectTraces: undefined` literal) keeps the
    // absent-channel case omitting the field under exactOptionalPropertyTypes. Without
    // this pass-through the field would reach buildUIState but be dropped at the filter
    // whitelist — the shipped EC-206 failure mode.
    if (uiState.effectTraces !== undefined) {
        const effectTracesCopy = [];
        for (const trace of uiState.effectTraces) {
            const traceCopy = {
                cardId: trace.cardId,
                scope: trace.scope,
                timing: trace.timing,
                effect: trace.effect,
                handler: trace.handler,
                status: trace.status,
                fireSite: trace.fireSite,
                params: { ...trace.params },
                turn: trace.turn,
            };
            // why: WP-706 / D-24528 — carry the count-scaled `resolution` sub-record through the
            // audience filter with a FRESH-object copy (+ a fresh `countedInputs` array) for the
            // same aliasing defence (D-11105) and conditional assignment (absent ⇒ omitted). This
            // MUST mirror uiState.build.ts: a field built but NOT carried here is silently dropped
            // at the whitelist (the shipped EC-206 failure mode / Board-Visible Field Rule).
            // resolution is PUBLIC dispatch data (D-12803) — passed through value-unchanged for
            // every audience, redacting nothing.
            if (trace.resolution !== undefined) {
                const resolutionCopy = {
                    countSource: trace.resolution.countSource,
                    resource: trace.resolution.resource,
                    magnitude: trace.resolution.magnitude,
                    count: trace.resolution.count,
                    perEach: trace.resolution.perEach,
                    computedValue: trace.resolution.computedValue,
                };
                if (trace.resolution.countedInputs !== undefined) {
                    resolutionCopy.countedInputs = [...trace.resolution.countedInputs];
                }
                traceCopy.resolution = resolutionCopy;
            }
            effectTracesCopy.push(traceCopy);
        }
        result.effectTraces = effectTracesCopy;
    }
    // why: WP-802 / D-24689 §1 — isDebugMatch is PUBLIC to EVERY audience (own
    // player, other player, spectator), so every seat and every diagnostics export
    // shows that the match is an operator match. Conditional assignment keeps a
    // normal match omitting the field. Without this pass-through the field would be
    // dropped at the whitelist (the shipped EC-206 failure mode).
    if (uiState.isDebugMatch === true) {
        result.isDebugMatch = true;
    }
    return result;
}
/**
 * Redacts the Enigma Code for one audience (WP-830 / D-24763).
 *
 * @param enigmaCode - The full projection from buildUIState.
 * @param audience - The viewer.
 * @returns A fresh projection: unseen cards carry only `peekedBy`.
 */
function filterEnigmaCodeForAudience(enigmaCode, audience) {
    const cards = [];
    for (const card of enigmaCode.cards) {
        const isViewerAllowed = enigmaCode.isRevealed ||
            (audience.kind === 'player' && card.peekedBy.includes(audience.playerId));
        if (isViewerAllowed && card.display !== undefined) {
            cards.push({ peekedBy: [...card.peekedBy], display: { ...card.display }, colors: [...(card.colors ?? [])] });
        }
        else {
            cards.push({ peekedBy: [...card.peekedBy] });
        }
    }
    return { isRevealed: enigmaCode.isRevealed, cards };
}
//# sourceMappingURL=uiState.filter.js.map